/* eslint-disable react/prop-types */
"use client";

import React from "react";
import { useTranslation } from "react-i18next";
import AppModalShell, {
  AppModalButton,
  AppModalContentItem,
} from "../common/AppModalShell";
import PremiumCodeLabel from "../common/PremiumCodeLabel";
import {
  DELETED_USER_LABEL,
  formatUserHandle,
  isDeletedAccountUser,
} from "../../utils/deletedAccount";
import { resolveUserAvatarSrc } from "../../utils/userAvatar";

function resolveParticipantAvatar(participant) {
  return resolveUserAvatarSrc(participant);
}

function getParticipantCode(participant) {
  return String(participant?.code || participant?.user?.code || "").trim().toUpperCase();
}

function getParticipantUsername(participant) {
  if (!participant) return "";
  if (isDeletedAccountUser(participant)) return DELETED_USER_LABEL;
  const raw = String(
    participant?.username ||
      participant?.user?.username ||
      participant?.owner_username ||
      ""
  ).trim();
  return raw ? formatUserHandle(raw) : "";
}

export default function ChatParticipantReportModal({
  visible = false,
  selectedParticipant = null,
  comment = "",
  desktopMountNode = null,
  canSelect = true,
  submitting = false,
  onCommentChange = () => {},
  onClose = () => {},
  onStartSelecting = () => {},
  onConfirm = () => {},
}) {
  const { t } = useTranslation("menu");
  const copyBase = "chats.event_chat.user_report";
  const code = getParticipantCode(selectedParticipant);
  const username = getParticipantUsername(selectedParticipant);
  const avatarSrc = resolveParticipantAvatar(selectedParticipant);
  const hasSelectedParticipant = Boolean(code);
  const trimmedComment = String(comment || "").trim();

  return (
    <AppModalShell
      open={visible}
      title={t(`${copyBase}.title`)}
      description={t(`${copyBase}.description`)}
      desktopMountNode={desktopMountNode}
      onClose={onClose}
      closeAriaLabel={t(`${copyBase}.close_aria`)}
      maxWidthClassName="max-w-[35rem]"
      panelClassName="border-white/64"
      bodyClassName="px-5 pb-5"
    >
      {hasSelectedParticipant ? (
        <AppModalContentItem className="rounded-[24px] border border-white/10 bg-white/[0.07] px-4 py-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
          <h3 className="text-center text-[16px] font-bold leading-5 text-white">
            {t(`${copyBase}.selected_label`)}
          </h3>
          <div className="mt-3 flex items-center rounded-bl-[45px] rounded-br-[18px] rounded-tl-[45px] rounded-tr-[18px] border border-white/18 bg-white/[0.06] px-3 py-3 text-left">
            <span className="mr-3 flex h-[54px] w-[54px] shrink-0 items-center justify-center overflow-hidden rounded-full border border-white/70 bg-white/12">
              {avatarSrc ? (
                <img
                  src={avatarSrc}
                  alt={code}
                  className="h-full w-full object-cover"
                  loading="lazy"
                  onError={(event) => {
                    event.currentTarget.style.display = "none";
                  }}
                />
              ) : null}
            </span>
            <span className="min-w-0 flex-1">
              <PremiumCodeLabel
                as="span"
                code={code}
                isPremium={Boolean(
                  selectedParticipant?.has_premium_subscription ||
                    selectedParticipant?.user?.has_premium_subscription
                )}
                className="truncate text-[21px] font-black leading-[24px] text-white"
              />
              {username ? (
                <span className="mt-[2px] block truncate text-[15px] font-semibold leading-[18px] text-white/82">
                  {username}
                </span>
              ) : null}
            </span>
          </div>
        </AppModalContentItem>
      ) : null}

      {hasSelectedParticipant ? (
        <AppModalContentItem className="mt-5">
          <label className="block text-sm font-semibold text-white/90">
            {t(`${copyBase}.reason_label`)}
          </label>
          <textarea
            value={comment}
            onChange={(event) => onCommentChange(event.target.value)}
            placeholder={t(`${copyBase}.reason_placeholder`)}
            className="mt-2 min-h-[118px] w-full resize-none rounded-[18px] border border-white/12 bg-white/10 px-4 py-3 text-sm font-medium leading-5 text-white outline-none placeholder:text-white/42"
          />
        </AppModalContentItem>
      ) : null}

      <AppModalContentItem className={`${hasSelectedParticipant ? "mt-5" : "mt-1"} grid grid-cols-1 gap-3`}>
        {canSelect ? (
          <AppModalButton
            variant="secondary"
            onClick={onStartSelecting}
            disabled={submitting}
            loading={submitting}
          >
            {hasSelectedParticipant
              ? t(`${copyBase}.change_user`)
              : t(`${copyBase}.select_user`)}
          </AppModalButton>
        ) : null}

        {hasSelectedParticipant ? (
          <AppModalButton
            variant="primary"
            onClick={onConfirm}
            disabled={!trimmedComment || submitting}
            loading={submitting}
          >
            {submitting
              ? t(`${copyBase}.confirm_loading`)
              : t(`${copyBase}.confirm`)}
          </AppModalButton>
        ) : null}
      </AppModalContentItem>
    </AppModalShell>
  );
}
